import { useEffect, useRef, useState } from 'react';
import type { ErrorComponentProps } from '@tanstack/react-router';
import {
  ErrorScreen,
  NoConnectionScreen as UiNoConnectionScreen,
  NotFoundScreen as UiNotFoundScreen,
  ServerErrorScreen as UiServerErrorScreen,
  SessionExpiredScreen as UiSessionExpiredScreen,
  StaleVersionScreen as UiStaleVersionScreen,
} from '@eureca/ui';
import { isChunkLoadFailure, shouldReloadForChunkFailure } from '@/lib/chunk-load-failure';
import { reportRouterError, type ClientErrorClassification } from '@/lib/client-error-report';
import { noteServerError } from '@/lib/router-error-retry';

/**
 * EU-636 (UNI-016..019) / EU-711 — telas de erro do App Candidato.
 *
 * EU-686 (RN-21) promoveu a implementação para `@eureca/ui`
 * (`sections/error-screens`, com a propriedade de plataforma — o App
 * Consultor consome a variante `consultants`). Este arquivo vira SHIM
 * (padrão V50/PdfViewer): preserva a API e os defaults dos call-sites do
 * candidato — testes e consumidores intactos. Não reimplemente aqui.
 */

export { ErrorScreen };

const goHome = () => {
  window.location.href = '/';
};
const reload = () => {
  window.location.reload();
};

/** UNI-016 — 404 (rota inexistente). */
export function NotFoundScreen() {
  return <UiNotFoundScreen platform="candidate" onGoHome={goHome} />;
}

/**
 * UNI-017 — erro de servidor (500-503).
 *
 * EU-747 — `onResume` (opcional) é a retomada da inscrição ("Voltar às etapas"):
 * com ele, "Voltar ao início" desce para ação terciária — nunca é a única saída.
 */
export function ServerErrorScreen({
  onRetry = reload,
  onResume,
}: {
  onRetry?: () => void;
  onResume?: () => void;
}) {
  return (
    <UiServerErrorScreen
      platform="candidate"
      onRetry={onRetry}
      onGoHome={goHome}
      onResume={onResume}
    />
  );
}

/** UNI-018 / EU-711 (OFF-002) — sem conexão de internet. */
export function NoConnectionScreen({ onRetry = reload }: { onRetry?: () => void }) {
  return <UiNoConnectionScreen platform="candidate" onRetry={onRetry} />;
}

/**
 * DEV-95 (caso B) — o chunk da rota não chegou e o recarregamento automático já
 * foi gasto. `onRetry` default é `reload` DE VERDADE, nunca o `reset()` do
 * router.
 */
export function StaleVersionScreen({ onReload = reload }: { onReload?: () => void }) {
  return <UiStaleVersionScreen platform="candidate" onReload={onReload} />;
}

/** UNI-019 — sessão expirada. */
export function SessionExpiredScreen({ onLogin }: { onLogin: () => void }) {
  return <UiSessionExpiredScreen platform="candidate" onLogin={onLogin} />;
}

/** EU-747 — `vagaId` só vai para a URL de retomada se tiver a forma de um id. */
const RESUME_JOB_ID_PATTERN = /^[A-Za-z0-9-]{1,64}$/;

/**
 * EU-747 — a vaga da URL atual (`?vagaId=`), para "Voltar às etapas". Lê de
 * `window.location` e não do router de propósito: o componente de erro pode
 * estar fora de uma match válida, e a navegação de retomada é COMPLETA
 * (`location.assign`) — além de devolver o candidato às etapas, carrega o build
 * novo, que é o que cura a aba presa numa implantação antiga.
 */
function readResumeJobId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = new URLSearchParams(window.location.search).get('vagaId');
    return raw !== null && RESUME_JOB_ID_PATTERN.test(raw) ? raw : null;
  } catch {
    return null;
  }
}

function currentPathname(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

function errorMessageOf(error: unknown): string {
  if (typeof error === 'string') return error;
  const message = (error as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' ? message : String(error);
}

/**
 * Componente de erro do router (`defaultErrorComponent`): escolhe entre "sem
 * conexão" (offline), "chunk da rota não chegou" (DEV-95 caso B) e "erro de
 * servidor" (500-503).
 *
 * A ordem dos ramos é deliberada. `navigator.onLine === false` vem primeiro
 * porque é o diagnóstico mais específico e o candidato precisa de ação dele
 * (conferir a conexão) antes de qualquer outra coisa — e recarregar sem rede não
 * ia a lugar nenhum. Falha de chunk vem antes de erro de servidor porque a tela
 * de servidor oferece um `reset()` que, para falha de módulo, é o único retry que
 * NÃO pode funcionar: o browser cacheia falha de módulo no *module map* e a
 * segunda tentativa falha em 0 ms sem emitir request (medido em prd). Erro de
 * servidor fica no fundo, com o `reset` intacto — para um 5xx transitório é a
 * escolha certa, e recarregar a página inteira seria pior (EU-636 / UNI-017).
 *
 * EU-747 — três coisas a mais neste ramo de servidor, porque um erro que o
 * classificador NÃO reconhece (a mensagem de MIME do WebKit ficou um ciclo
 * inteiro nessa situação) não pode virar beco sem saída:
 *
 * 1. **Escalada do retry**: o 1º "Tentar novamente" é `reset()`; se a MESMA
 *    mensagem voltar na MESMA rota dentro de 60 s (o `lazyRouteComponent`
 *    relançou o erro guardado), o próximo clique recarrega a página.
 * 2. **Retomada**: com `?vagaId=` na URL, "Voltar às etapas" leva a
 *    `/vagas/:id/processo` por navegação completa; "Voltar ao início" vira
 *    ação terciária.
 * 3. **Telemetria**: TODA renderização desta tela é relatada ao BFF
 *    (`POST /me/client-errors`, fire-and-forget) com classificação, rota,
 *    build e a mensagem crua — é assim que a próxima variante vira assinatura.
 */
export function RouterErrorScreen({ error, reset }: ErrorComponentProps) {
  const chunkFailure = isChunkLoadFailure(error);
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  // Só decide (e grava a marca) quando é falha de chunk: `shouldReload…` tem
  // efeito colateral e não pode ser consultado no caminho do 5xx. EU-747 — nem
  // sem rede: recarregar offline não ia a lugar nenhum e ainda gastava a marca
  // do cooldown, deixando a ocorrência real (com rede) sem o reload automático.
  const autoReload = !offline && chunkFailure && shouldReloadForChunkFailure();
  const route = currentPathname();

  // EU-747 (1) — decidido UMA vez por montagem (inicializador lazy): a tela
  // desmonta no `reset()`, então a próxima ocorrência é uma instância nova e
  // volta a consultar o histórico. Só o ramo de servidor conta.
  const [repeat] = useState(
    () => !offline && !chunkFailure && noteServerError(`${route}|${errorMessageOf(error)}`).repeat
  );

  const classification: ClientErrorClassification = offline
    ? 'offline'
    : chunkFailure
      ? 'chunk'
      : 'server';

  // EU-747 (3) — um relato por montagem. O ref segura o double-invoke do
  // StrictMode em dev e qualquer re-render sem remontar.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (reportedRef.current) return;
    reportedRef.current = true;
    void reportRouterError({ error, classification, route, online: !offline, repeat });
  }, [error, classification, route, offline, repeat]);

  useEffect(() => {
    // O recarregamento roda em EFEITO, não no corpo do render: efeito colateral
    // durante o render quebra o StrictMode (que renderiza duas vezes) e, em
    // produção, dispararia antes de o React terminar de comprometer a árvore.
    if (autoReload) window.location.reload();
  }, [autoReload]);

  if (offline) {
    return <NoConnectionScreen onRetry={reset} />;
  }
  if (chunkFailure) {
    // Enquanto o reload não acontece a tela já é a certa: se o navegador demorar
    // a atender, o candidato não vê "Algo deu errado" piscando no meio.
    return <StaleVersionScreen />;
  }

  // EU-747 (2) — retomada pela URL.
  const resumeJobId = readResumeJobId();
  return (
    <ServerErrorScreen
      onRetry={repeat ? reload : reset}
      onResume={
        resumeJobId ? () => window.location.assign(`/vagas/${resumeJobId}/processo`) : undefined
      }
    />
  );
}
